import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Sparkles, Wrench, ArrowRight, ExternalLink, ShieldCheck, Layers, Cpu, Code2, Paintbrush } from 'lucide-react';
import Navigation from '../components/Navigation';
import ThreeBackground from '../components/ThreeBackground';
import Modal from '../components/Modal';
import NexaLogoIcon from '../components/NexaLogoIcon';
import MetaSEO from '../components/MetaSEO';

import SmartAssistant from '../components/SmartAssistant';
import BackgroundRemover from '../components/BackgroundRemover';
import AIImageEditor from '../components/AIImageEditor';
import AIChatAvatar from '../components/Aichatavatar';
import PasswordGenerator from '../components/PasswordGenerator';
import QRCodeGenerator from '../components/QRCodeGenerator';
import TextCaseConverter from '../components/TextCaseConverter';
import UnitConverter from '../components/UnitConverter';
import ColorPaletteGenerator from '../components/ColorPaletteGenerator';
import LoremGenerator from '../components/LoremGenerator';
import TextDiffChecker from '../components/Textdiffchecker';

import logo from "../images/logo.png";
import './ToolsPage.css';

const ToolsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', component: null });

  const toolsList = [
    {
      id: 'smart-assistant',
      title: 'Smart Shopping Assistant',
      description: 'AI-powered purchasing recommendation engine finding verified, top-tier product deals in real time.',
      icon: <Sparkles className="tool-card-icon" />,
      category: 'ai',
      badge: 'Popular AI',
      Component: SmartAssistant
    },
    {
      id: 'bg-remover',
      title: 'AI Background Remover',
      description: 'Instant client-side background removal algorithm powered by AI computer vision models.',
      icon: <Layers className="tool-card-icon" />,
      category: 'ai',
      badge: 'Vision AI',
      Component: BackgroundRemover
    },
    {
      id: 'image-editor',
      title: 'AI Image Studio & Filters',
      description: 'Neural image manipulation, dynamic RGB color matrix filters, and pixel-level enhancements.',
      icon: <Paintbrush className="tool-card-icon" />,
      category: 'ai',
      badge: 'Creative AI',
      Component: AIImageEditor
    },
    {
      id: 'ai-avatar',
      title: 'AI Persona & Voice Chat Avatar',
      description: 'Interactive conversational avatar agent with natural speech synthesis and custom persona profiles.',
      icon: <Cpu className="tool-card-icon" />,
      category: 'ai',
      badge: 'Agent AI',
      Component: AIChatAvatar
    },
    {
      id: 'password-gen',
      title: 'Secure Password Generator',
      description: 'Generate high-entropy cryptographic passwords with customizable character set rules.',
      icon: <ShieldCheck className="tool-card-icon" />,
      category: 'security',
      badge: 'Security',
      Component: PasswordGenerator
    },
    {
      id: 'qr-gen',
      title: 'Dynamic QR Code Engine',
      description: 'Generate vector QR codes for URLs, WiFi credentials, contact cards, and text payloads.',
      icon: <Code2 className="tool-card-icon" />,
      category: 'utility',
      badge: 'Generator',
      Component: QRCodeGenerator
    },
    {
      id: 'text-diff',
      title: 'Code & Text Diff Checker',
      description: 'Side-by-side split visual diff inspection to compare file variations and code commits.',
      icon: <Code2 className="tool-card-icon" />,
      category: 'dev',
      badge: 'Developer',
      Component: TextDiffChecker
    },
    {
      id: 'case-converter',
      title: 'Text Case & Grammar Suite',
      description: 'Transform string capitalization (camelCase, snake_case, PascalCase, UPPERCASE) instantly.',
      icon: <Wrench className="tool-card-icon" />,
      category: 'utility',
      badge: 'Text Tool',
      Component: TextCaseConverter
    },
    {
      id: 'unit-converter',
      title: 'Universal Unit Converter',
      description: 'Convert values across metric, imperial, temperature, weight, speed, and digital data.',
      icon: <Wrench className="tool-card-icon" />,
      category: 'utility',
      badge: 'Calculator',
      Component: UnitConverter
    },
    {
      id: 'color-palette',
      title: 'Color Palette & Extractor',
      description: 'Generate harmonious color palettes and copy CSS hex, RGB, and HSL codes with one click.',
      icon: <Paintbrush className="tool-card-icon" />,
      category: 'design',
      badge: 'Design System',
      Component: ColorPaletteGenerator
    },
    {
      id: 'lorem-gen',
      title: 'Lorem Ipsum Mock Generator',
      description: 'Generate clean placeholder prose, paragraphs, or sentence lists for UI prototypes.',
      icon: <Wrench className="tool-card-icon" />,
      category: 'utility',
      badge: 'Prototyping',
      Component: LoremGenerator
    }
  ];

  const filteredTools = useMemo(() => {
    return toolsList.filter((tool) => {
      const matchesCategory = activeCategory === 'all' || tool.category === activeCategory;
      const matchesQuery = tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           tool.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesQuery;
    });
  }, [activeCategory, searchQuery]);

  const openModal = (title, ToolComponent) => {
    if (!ToolComponent) return;
    setModalContent({ title, component: <ToolComponent /> });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => setModalContent({ title: '', component: null }), 300);
  };

  return (
    <div className="tools-page">
      <MetaSEO 
        title="Interactive AI & Developer Tools Hub"
        description="Free web developer suite: AI background remover, image enhancer, password generator, QR code engine, diff checker, color extractor & unit converter."
        keywords="NexaAI Tools Hub, AI background remover, password generator, QR code generator, text diff checker, color palette generator, unit converter"
      />
      <ThreeBackground />
      <Navigation />

      <main className="tools-main container">
        {/* Header Banner */}
        <div className="tools-hero">
          <div className="badge-pill">
            <span className="pill-dot"></span>
            NexaAI Tools Hub
          </div>
          <h1 className="tools-title">
            Enterprise AI & <span className="gradient-text">Developer Suite</span>
          </h1>
          <p className="tools-subtitle">
            Explore our curated suite of high-performance client-side AI modules, utility generators, and dev tools engineered for speed and privacy.
          </p>

          {/* Search & Filter Bar */}
          <div className="tools-controls">
            <div className="search-box">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Search tools by name, utility, or feature..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="clear-search" onClick={() => setSearchQuery('')}>×</button>
              )}
            </div>

            <div className="category-tabs">
              <button
                className={`tab-btn ${activeCategory === 'all' ? 'active' : ''}`}
                onClick={() => setActiveCategory('all')}
              >
                All Tools ({toolsList.length})
              </button>
              <button
                className={`tab-btn ${activeCategory === 'ai' ? 'active' : ''}`}
                onClick={() => setActiveCategory('ai')}
              >
                AI Products
              </button>
              <button
                className={`tab-btn ${activeCategory === 'utility' ? 'active' : ''}`}
                onClick={() => setActiveCategory('utility')}
              >
                Developer Utilities
              </button>
              <button
                className={`tab-btn ${activeCategory === 'design' ? 'active' : ''}`}
                onClick={() => setActiveCategory('design')}
              >
                Design & Color
              </button>
            </div>
          </div>
        </div>

        {/* Tools Grid */}
        {filteredTools.length > 0 ? (
          <div className="tools-hub-grid">
            {filteredTools.map((tool) => (
              <div key={tool.id} className="tool-hub-card">
                <div className="card-top">
                  <div className="icon-wrapper">
                    {tool.icon}
                  </div>
                  <span className="tool-badge">{tool.badge}</span>
                </div>
                <h3 className="card-title">{tool.title}</h3>
                <p className="card-desc">{tool.description}</p>

                <div className="card-footer">
                  {tool.link ? (
                    <Link to={tool.link} className="launch-btn">
                      <span>Open Workspace</span>
                      <ArrowRight size={16} />
                    </Link>
                  ) : (
                    <button
                      className="launch-btn"
                      onClick={() => openModal(tool.title, tool.Component)}
                    >
                      <span>Launch Tool</span>
                      <ArrowRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-results">
            <p>No tools matched your search query "{searchQuery}".</p>
            <button className="btn btn-secondary" onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}>
              Reset Filters
            </button>
          </div>
        )}
      </main>

      {/* Shared Modal */}
      <Modal isOpen={modalOpen} onClose={closeModal} title={modalContent.title}>
        {modalContent.component}
      </Modal>

      {/* Footer */}
      <footer className="footer-light">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <Link to="/" className="logo">
                <NexaLogoIcon size={38} />
                <div className="logo-text">
                  <span className="logo-main">Nexa<span className="orange-text">AI</span></span>
                  <span className="logo-sub">STUDIO</span>
                </div>
              </Link>
              <p className="footer-tagline">Architecting high-performance digital tools</p>
            </div>
            <div className="footer-links">
              <Link to="/">Home</Link>
              <Link to="/tools">Tools Hub</Link>
              <Link to="/contact">Contact</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} NexaAI Studio. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ToolsPage;
